/**
 * Telemetry helpers for the MIDI Quality Feedback flow.
 *
 * Spec: `docs/spec/midi-quality-feedback.md` §7.
 *
 * This module centralises the event names + payload shapes of that one flow;
 * {@link dispatch} forwards them to the product analytics client
 * (`lib/analytics`), which owns queuing, batching and identity.
 */
import { track } from "@/lib/analytics";
import type { VirtuosaEvent } from "@/lib/analytics/events";
import type { QualityFlag } from "@/lib/music/analyzer-types";

/** Reason key derived from `QualityStatus.flags`, with priority resolution. */
export type NeedsHelpReason =
  | "no_piano"
  | "drums_dominant"
  | "arrangement"
  | "default";

/**
 * Resolves the human-facing wording variant (and telemetry `reason`) from the
 * raw flag array. Priority order matches the spec §6.4:
 *   `no_piano` > `drums_dominant` > `arrangement` > `default`.
 */
export function resolveNeedsHelpReason(flags: readonly QualityFlag[]): NeedsHelpReason {
  if (flags.includes("no_piano") || flags.includes("all_drums")) {
    return "no_piano";
  }

  if (flags.includes("drums_dominant")) {
    return "drums_dominant";
  }

  if (flags.includes("arrangement")) {
    return "arrangement";
  }

  return "default";
}

// ─── Event payload contracts (1:1 with spec §7) ─────────────────────

/*
  `fileId` : identifiant opaque de l'import, tiré une seule fois par fichier
  reçu et porté à l'identique par les huit événements du flux.

  Il transportait le **nom du fichier** de la personne. Un nom de fichier
  nomme souvent quelqu'un, et celui-ci partait tel quel chez un sous-traitant,
  sur une propriété de cardinalité illimitée que personne ne pouvait ventiler.
  Or ce que le champ sert à faire, c'est joindre entre eux les événements d'un
  même import : un identifiant tiré au sort le fait aussi bien, et ne dit rien
  d'autre.

  Le piège est qu'il n'en existe qu'un par import : si deux surfaces le
  fabriquaient chacune de leur côté, l'abandon ne se joindrait jamais aux sept
  autres et le champ perdrait sa seule raison d'être. Il est donc tiré là où le
  fichier est reçu (`UploadModal`) et descend en prop.

  Seul `midi_quality_ok_with_hints_toast_shown` fait exception : ce flux-là
  démarre au studio, après persistance, et porte l'identifiant du MIDI en base.
*/

export interface NeedsHelpShownPayload {
  fileId: string;
  reason: NeedsHelpReason;
  flags: readonly QualityFlag[];
  trackCount: number;
  probableCount: number;
  durationSec: number;
  totalNotes: number;
}

export interface AuditionGlobalPlayedPayload {
  fileId: string;
  msSinceShown: number;
  samplesLoadMs: number | null;
}

export interface AuditionSoloPlayedPayload {
  fileId: string;
  trackIndex: number;
  programGM: number;
  isProbable: boolean;
  msSinceShown: number;
}

export interface SelectionChangedPayload {
  fileId: string;
  trackIndex: number;
  checked: boolean;
  isProbable: boolean;
  currentSelectedCount: number;
}

export interface OtherTracksExpandedPayload {
  fileId: string;
  otherCount: number;
  msSinceShown: number;
}

export interface ValidatedPayload {
  fileId: string;
  reason: NeedsHelpReason;
  finalSelection: readonly number[];
  probableCount: number;
  probableSelectedCount: number;
  otherSelectedCount: number;
  auditionGlobalUsed: boolean;
  auditionSoloCount: number;
  msSinceShown: number;
}

export interface BypassedPayload {
  fileId: string;
  reason: NeedsHelpReason;
  msSinceShown: number;
}

export interface AbandonedPayload {
  fileId: string;
  reason: NeedsHelpReason;
  exitVia: "close" | "retry" | "navigation";
  msSinceShown: number;
}

/**
 * Pourquoi la banque de sons de l'écoute n'a pas chargé, en vocabulaire fermé.
 *
 * `error` était le message d'exception brut : selon le navigateur et le CDN, la
 * chaîne pouvait contenir une URL d'infrastructure ou une phrase localisée. Une
 * propriété à cardinalité non bornée ne se ventile pas, alors que la décision
 * qu'on prend derrière tient en trois : allonger le délai, réparer la
 * livraison, ou décoder autrement.
 */
export type SamplesLoadFailureReason =
  | "timeout"
  | "network"
  | "decode_failed"
  | "unknown";

export interface SamplesLoadFailedPayload {
  fileId: string;
  error: SamplesLoadFailureReason;
  durationMs: number;
  retryAttempt: number;
}

/**
 * Emitted at the Studio mount when the file was opened directly under
 * `quality.kind === "ok_with_hints"` and we surfaced the non-blocking toast.
 * `fileId` is optional, the studio-page only receives the persisted MIDI id
 * after the inspect/analyze pipeline succeeds, image uploads or future
 * sources may lack it.
 */
export interface OkWithHintsToastShownPayload {
  fileId?: string;
  hints: readonly QualityFlag[];
  resolvedMessage: string;
}

/** Tagged union over every event the flow can emit. */
export type MidiQualityEvent =
  | { type: "midi_quality_needs_help_shown"; payload: NeedsHelpShownPayload }
  | { type: "midi_quality_audition_global_played"; payload: AuditionGlobalPlayedPayload }
  | { type: "midi_quality_audition_solo_played"; payload: AuditionSoloPlayedPayload }
  | { type: "midi_quality_selection_changed"; payload: SelectionChangedPayload }
  | { type: "midi_quality_other_tracks_expanded"; payload: OtherTracksExpandedPayload }
  | { type: "midi_quality_validated"; payload: ValidatedPayload }
  | { type: "midi_quality_bypassed"; payload: BypassedPayload }
  | { type: "midi_quality_abandoned"; payload: AbandonedPayload }
  | { type: "midi_quality_samples_load_failed"; payload: SamplesLoadFailedPayload }
  | { type: "midi_quality_ok_with_hints_toast_shown"; payload: OkWithHintsToastShownPayload };

/**
 * Single dispatch sink, wired to the product analytics client
 * (`lib/analytics`). Exactly as designed: the call sites never had to change.
 *
 * The cast bridges two shapes of the same union — `MidiQualityEvent` pairs
 * `type`/`payload`, `VirtuosaEvent` pairs `name`/`properties`. TypeScript
 * cannot carry the discriminant correlation across the rename, but the
 * mapping is 1:1 by construction (`lib/analytics/events.ts` reuses the very
 * payload types declared above).
 */
function dispatch(event: MidiQualityEvent): void {
  track({ name: event.type, properties: event.payload } as VirtuosaEvent);
}

// ─── Typed emitters ─────────────────────────────────────────────────

export function trackNeedsHelpShown(payload: NeedsHelpShownPayload): void {
  dispatch({ type: "midi_quality_needs_help_shown", payload });
}

export function trackAuditionGlobalPlayed(payload: AuditionGlobalPlayedPayload): void {
  dispatch({ type: "midi_quality_audition_global_played", payload });
}

export function trackAuditionSoloPlayed(payload: AuditionSoloPlayedPayload): void {
  dispatch({ type: "midi_quality_audition_solo_played", payload });
}

export function trackSelectionChanged(payload: SelectionChangedPayload): void {
  dispatch({ type: "midi_quality_selection_changed", payload });
}

export function trackOtherTracksExpanded(payload: OtherTracksExpandedPayload): void {
  dispatch({ type: "midi_quality_other_tracks_expanded", payload });
}

export function trackValidated(payload: ValidatedPayload): void {
  dispatch({ type: "midi_quality_validated", payload });
}

export function trackBypassed(payload: BypassedPayload): void {
  dispatch({ type: "midi_quality_bypassed", payload });
}

export function trackAbandoned(payload: AbandonedPayload): void {
  dispatch({ type: "midi_quality_abandoned", payload });
}

export function trackSamplesLoadFailed(payload: SamplesLoadFailedPayload): void {
  dispatch({ type: "midi_quality_samples_load_failed", payload });
}

export function trackOkWithHintsToastShown(payload: OkWithHintsToastShownPayload): void {
  dispatch({ type: "midi_quality_ok_with_hints_toast_shown", payload });
}
